import { TokenService } from 'abp-ng2-module';
import { LogService } from 'abp-ng2-module';
import { MessageService } from 'abp-ng2-module';
import { UtilsService } from 'abp-ng2-module';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppConsts } from '@shared/AppConsts';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import { AuthenticateModel, AuthenticateResultModel, ExternalAuthenticateModel, ExternalAuthenticateResultModel, ExternalLoginProviderInfoModel, PasswordlessAuthenticateModel, SocialSigninModel, TokenAuthServiceProxy } from '@shared/service-proxies/service-proxies';
import * as _ from 'lodash';
import { finalize } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { Subject } from '@node_modules/rxjs';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';

declare const FB: any; // Facebook API
declare const gapi: any; // Facebook API
declare const WL: any; // Microsoft API

export class ExternalLoginProvider extends ExternalLoginProviderInfoModel {

    static readonly FACEBOOK: string = 'Facebook';
    static readonly GOOGLE: string = 'Google';
    static readonly MICROSOFT: string = 'Microsoft';

    icon: string;
    initialized = false;

    constructor(providerInfo: ExternalLoginProviderInfoModel) {
        super();

        this.name = providerInfo.name;
        this.clientId = providerInfo.clientId;
        this.icon = ExternalLoginProvider.getSocialIcon(this.name);
    }

    private static getSocialIcon(providerName: string): string {
        providerName = providerName.toLowerCase();

        if (providerName === 'google') {
            providerName = 'googleplus';
        }

        return providerName;
    }
}

@Injectable()
export class LoginService {

    static readonly twoFactorRememberClientTokenName = 'TwoFactorRememberClientToken';

    authenticateModel: AuthenticateModel;
    socialSigninModel: SocialSigninModel;

    authenticateResult: AuthenticateResultModel;

    externalLoginProviders: ExternalLoginProvider[] = [];
    private _loginResultSubject = new Subject<AuthenticateResultModel>();
    loginResult$ = this._loginResultSubject.asObservable();

    rememberMe: boolean = true;

    constructor(
        private _tokenAuthService: TokenAuthServiceProxy,
        private _router: Router,
        private _utilsService: UtilsService,
        private _tokenService: TokenService,
        private _logService: LogService,
        private _appAuthService: AppAuthService
    ) {
        this.clear();
    }

    authenticate(finallyCallback?: () => void, redirectUrl?: string, isFromRegister: boolean = false, isFromPopup: boolean = false): void {
        finallyCallback = finallyCallback || (() => { });
        // We may switch to localStorage instead of cookies
        this.authenticateModel.twoFactorRememberClientToken = this._utilsService.getCookieValue(LoginService.twoFactorRememberClientTokenName);
        this.authenticateModel.singleSignIn = UrlHelper.getSingleSignIn();
        this.authenticateModel.returnUrl = UrlHelper.getReturnUrl();

        this._tokenAuthService
            .authenticate(this.authenticateModel)
            .pipe(finalize(finallyCallback))
            .subscribe((result: AuthenticateResultModel) => {
                this.processAuthenticateResult(result, redirectUrl, isFromRegister, isFromPopup);
            });
    }

    socialSignin(finallyCallback?: () => void, redirectUrl?: string, isFromRegister: boolean = false, isFromPopup: boolean = false): void {
        finallyCallback = finallyCallback || (() => { });
        // We may switch to localStorage instead of cookies
        this.socialSigninModel.twoFactorRememberClientToken = this._utilsService.getCookieValue(LoginService.twoFactorRememberClientTokenName);
        this.socialSigninModel.singleSignIn = UrlHelper.getSingleSignIn();
        this.socialSigninModel.returnUrl = UrlHelper.getReturnUrl();

        this._tokenAuthService
            .socialSignin(this.socialSigninModel)
            .pipe(finalize(finallyCallback))
            .subscribe((result: AuthenticateResultModel) => {
                this.processAuthenticateResult(result, redirectUrl, isFromRegister, isFromPopup);
            });
    }

    passwordlessAuthenticate(finallyCallback?: (isError: boolean) => void, passwordlessAuthenticateModel?: PasswordlessAuthenticateModel, redirectUrl?: string, isFromRegister: boolean = false, isFromPopup: boolean = false): void {
        finallyCallback = finallyCallback || ((isError: boolean = false) => { });

        const self = this;

        self._tokenAuthService.passwordlessAuthenticate(passwordlessAuthenticateModel).subscribe({
            next: (result: AuthenticateResultModel) => {
                self.processAuthenticateResult(result, redirectUrl, isFromRegister, isFromPopup);
                finallyCallback(false);
            },
            error: (err: any) => {
                finallyCallback(true);
            },
        });
    }

    init(): void {
        this.initExternalLoginProviders();
    }

    private processAuthenticateResult(authenticateResult: AuthenticateResultModel, redirectUrl?: string, isFromRegister: boolean = false, isFromPopup: boolean = false) {
        this.authenticateResult = authenticateResult;

        if (authenticateResult.shouldResetPassword) {
            // Password reset

            if (!isFromPopup) {
                this._router.navigate(['account/reset-password'], {
                    queryParams: {
                        userId: authenticateResult.userId,
                        tenantId: abp.session.tenantId,
                        resetCode: authenticateResult.passwordResetCode
                    }
                });
            }

            this.clear();

            if (isFromPopup) {
                this._loginResultSubject.next(authenticateResult);
            }

        } else if (authenticateResult.requiresTwoFactorVerification) {
            // Two factor authentication
            if (!isFromPopup) {
                this._router.navigate(['account/send-code']);
            }
            if (isFromPopup) {
                this._loginResultSubject.next(authenticateResult);
            }

        } else if (authenticateResult.accessToken) {
            // Successfully logged in
            if (authenticateResult.returnUrl && !redirectUrl) {
                redirectUrl = authenticateResult.returnUrl;
            }

            this.login(
                authenticateResult.accessToken,
                authenticateResult.encryptedAccessToken,
                authenticateResult.expireInSeconds,
                authenticateResult.isExternalLogin == true ? true : this.rememberMe,
                authenticateResult.twoFactorRememberClientToken,
                redirectUrl,
                authenticateResult.productLiftSSOToken,
                isFromRegister,
                isFromPopup,    
                authenticateResult.requireEmailVerification
            );

            if (isFromPopup) {
                this._loginResultSubject.next(authenticateResult);
            }

        } else {
            // Unexpected result!

            this._logService.warn('Unexpected authenticateResult!');
            if (!isFromPopup) {
                this._router.navigate(['account/login']);
            }

            if (isFromPopup) {
                this._loginResultSubject.next(authenticateResult);
            }

        }
    }

    private login(accessToken: string, encryptedAccessToken: string, expireInSeconds: number, rememberMe?: boolean, twoFactorRememberClientToken?: string, redirectUrl?: string, productLiftSSOToken?: string, isFromRegister: boolean = false, isFromPopup: boolean = false, isEmailConfirmationRequired: boolean = false): void {
        let tokenExpireDate = rememberMe ? (new Date(new Date().getTime() + 1000 * expireInSeconds)) : undefined;
        const cookieName = environment.production ? 'Abp.AuthToken' : 'Abp.DevAuthToken';
        this._appAuthService.deleteSessionCookie(cookieName);
        document.cookie = `${cookieName}=${accessToken};expires=${tokenExpireDate};domain=${environment.LoginCookieDomain};path=/`;
        
        localStorage.setItem('IsEmailConfirmationRequired', isEmailConfirmationRequired ? "true" : "false");
            this._utilsService.setCookieValue(
            AppConsts.authorization.encrptedAuthTokenName,
            encryptedAccessToken,
            tokenExpireDate,
            abp.appPath
        );

        if (productLiftSSOToken) {
            this._utilsService.setCookieValue(
                AppConsts.productLife.ssoTokenKey,
                productLiftSSOToken,
                undefined,
                abp.appPath
            );
        }

        if (twoFactorRememberClientToken) {
            this._utilsService.setCookieValue(
                LoginService.twoFactorRememberClientTokenName,
                twoFactorRememberClientToken,
                new Date(new Date().getTime() + 365 * 86400000), // 1 year
                abp.appPath
            );
        }

        if (!isFromPopup) {
            if (redirectUrl) {
                if (isFromRegister) {
                    localStorage.setItem('isFromRegister', 'true');
                }
                location.href = redirectUrl;
            } else {
                let initialUrl = UrlHelper.initialUrl;

                if (initialUrl.indexOf('/account') > 0) {
                    initialUrl = AppConsts.appBaseUrl;
                }
                location.href = initialUrl;
            }
        }
    }

    public clear(): void {
        this.authenticateModel = new AuthenticateModel();
        this.authenticateModel.rememberClient = false;
        this.authenticateResult = null;
        this.rememberMe = true;
    }

    private initExternalLoginProviders() {
        this._tokenAuthService
            .getExternalAuthenticationProviders()
            .subscribe((providers: ExternalLoginProviderInfoModel[]) => {
                this.externalLoginProviders = _.map(providers, p => new ExternalLoginProvider(p));
            });
    }
}
